import React from 'react';
import {
  loadSiteMembershipDefaults,
  setSiteMembershipDefaults,
  SITE_MEMBERSHIP_DEFAULT_FALLBACK,
} from '../utils/siteDefaults';

/* ====================================================================
   SITE MEMBERSHIP DEFAULTS (super-admin)

   The three option lists behind every club's membership form. Age groups
   are the whole platform's list — a club cannot override them, so this is
   the only place they can be changed at all. The other two are the lists
   a new club starts from, and each club may then edit its own copy under
   its own Settings -> Membership.
   ==================================================================== */

const LISTS = [
  {
    key: 'ageGroupOptions',
    label: 'Age Groups',
    icon: 'fa-birthday-cake',
    /* Worth spelling out: an admin editing these expects a club-level
       default and would otherwise be surprised to change every club. */
    hint: 'Used by every club and not overridable. Members already recorded under a band you remove keep that value until they next answer.',
  },
  {
    key: 'membershipTypeOptions',
    label: 'Membership Types',
    icon: 'fa-id-card',
    hint: 'The starting list for a new club. Existing clubs keep whatever they have set.',
  },
  {
    key: 'playerTypeOptions',
    label: 'Player Types',
    icon: 'fa-users',
    hint: 'The starting list for a new club. Existing clubs keep whatever they have set.',
  },
];

function SiteMembershipDefaults({ API, showToast }) {
  const [form, setForm] = React.useState(SITE_MEMBERSHIP_DEFAULT_FALLBACK);
  const [drafts, setDrafts] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    loadSiteMembershipDefaults(true)
      .then((data) => { if (alive) setForm(data); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const addOption = (key) => {
    const value = (drafts[key] || '').trim();
    if (!value) return;
    if ((form[key] || []).includes(value)) {
      showToast(`"${value}" is already on that list`, 'amber');
      return;
    }
    setForm(f => ({ ...f, [key]: [...(f[key] || []), value] }));
    setDrafts(d => ({ ...d, [key]: '' }));
  };

  const removeOption = (key, value) => {
    setForm(f => {
      const next = (f[key] || []).filter(v => v !== value);
      /* An empty list would leave the dropdown blank on the public form, so
         the server would refuse it anyway - say so before the round trip. */
      if (!next.length) {
        showToast('A list must keep at least one option', 'amber');
        return f;
      }
      return { ...f, [key]: next };
    });
  };

  const moveOption = (key, index, offset) => {
    setForm(f => {
      const list = [...(f[key] || [])];
      const target = index + offset;
      if (target < 0 || target >= list.length) return f;
      [list[index], list[target]] = [list[target], list[index]];
      return { ...f, [key]: list };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/auth/site-membership-defaults`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Failed to save membership defaults');
      /* Push into the shared cache so every open form picks the new lists
         up without a reload. */
      setSiteMembershipDefaults(data);
      setForm(data);
      showToast('Membership defaults saved');
    } catch (err) {
      showToast(err.message || 'Failed to save membership defaults', 'red');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="gs-panel w3-margin-bottom w3-small w3-text-grey">Loading membership defaults...</div>;

  return (
    <div className="gs-panel w3-margin-bottom">
      <div className="w3-small w3-text-grey" style={{ marginBottom: '10px' }}>
        The option lists behind every club's membership form.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px' }}>
        {LISTS.map(list => (
          <div key={list.key} className="w3-border w3-round" style={{ padding: '10px', background: '#fff' }}>
            <label className="w3-tiny w3-text-grey">
              <i className={`fa ${list.icon} w3-margin-right`}></i>{list.label}
            </label>
            <div className="w3-tiny w3-text-grey" style={{ margin: '4px 0 8px' }}>{list.hint}</div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {(form[list.key] || []).map((opt, index) => (
                <div key={opt} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span className="w3-small" style={{ flex: 1 }}>{opt}</span>
                  <button type="button" className="w3-button w3-tiny w3-round w3-theme-l4" title="Move up"
                    disabled={index === 0}
                    onClick={() => moveOption(list.key, index, -1)}>
                    <i className="fa fa-arrow-up"></i>
                  </button>
                  <button type="button" className="w3-button w3-tiny w3-round w3-theme-l4" title="Move down"
                    disabled={index === (form[list.key] || []).length - 1}
                    onClick={() => moveOption(list.key, index, 1)}>
                    <i className="fa fa-arrow-down"></i>
                  </button>
                  <button type="button" className="w3-button w3-tiny w3-round w3-hover-red" title={`Remove ${opt}`} aria-label={`Remove ${opt}`}
                    onClick={() => removeOption(list.key, opt)}>
                    <i className="fa fa-times"></i>
                  </button>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
              <input className="w3-input w3-border w3-round w3-small" style={{ flex: 1 }}
                placeholder={`Add ${list.label.toLowerCase().replace(/s$/, '')}`}
                value={drafts[list.key] || ''}
                onChange={e => setDrafts(d => ({ ...d, [list.key]: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(list.key); } }} />
              <button type="button" className="w3-button w3-tiny w3-theme-l4 w3-round" onClick={() => addOption(list.key)}>
                Add
              </button>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
        <button className="w3-button w3-round" type="button" disabled={saving}
          style={{ minHeight: 'var(--gs-control-sm)', background: 'var(--gs-accent-fill, var(--club-30))', color: '#fff', fontWeight: 600 }}
          onClick={save}>
          {saving ? 'Saving...' : 'Save defaults'}
        </button>
        <button className="w3-button w3-round w3-theme-l4" type="button" disabled={saving}
          style={{ minHeight: 'var(--gs-control-sm)' }}
          onClick={() => setForm(SITE_MEMBERSHIP_DEFAULT_FALLBACK)}>
          Reset to shipped lists
        </button>
      </div>
    </div>
  );
}

export default SiteMembershipDefaults;
